import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { UserClassesService } from './user-classes.service';
import { AuthService } from './auth.service';
import { UserCourseDto, createUserCourseDto } from '@models/user-course-dto';

@Injectable({
  providedIn: 'root'
})
export class UserEnrolmentService {
  
  private enrollmentDataSubject = new BehaviorSubject<UserCourseDto | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(
    private userClassesService: UserClassesService,
    private authService: AuthService
  ) {}

  /**
   * Get the current enrollment data as an Observable
   */
  get enrollmentData$(): Observable<UserCourseDto | null> {
    return this.enrollmentDataSubject.asObservable();
  }

  /**
   * Get the loading state
   */
  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }

  /**
   * Get enrollment data - uses cache if available and fresh, otherwise fetches from API
   */
  getEnrollmentData(): Observable<UserCourseDto> {
    const cachedData = this.enrollmentDataSubject.value;
    const now = Date.now();
    
    // Return cached data if it exists and is fresh
    if (cachedData && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return of(cachedData);
    }

    // Fetch fresh data
    return this.fetchEnrollmentData();
  }

  /**
   * Force refresh of enrollment data from API
   */
  refreshEnrollmentData(): Observable<UserCourseDto> {
    return this.fetchEnrollmentData();
  }

  /**
   * Get enrollment data with fallback to defaults if not found
   */
  getEnrollmentDataWithFallback(): Observable<UserCourseDto> {
    return this.getEnrollmentData().pipe(
      catchError(() => {
        // Return default values if enrollment not found
        console.warn('Enrollment data not found, using defaults');
        const defaultData = createUserCourseDto({
          userId: this.authService.returnCurrentUser().id,
          courseId: this.authService.returnCourseId(),
          isCourseComplete: this.authService.getCourseCompleted()
        });
        
        // Cache the default data
        this.enrollmentDataSubject.next(defaultData);
        this.cacheTimestamp = Date.now();
        
        return of(defaultData);
      })
    );
  }

  /**
   * Clear cached data (useful on logout)
   */
  clearCache(): void {
    this.enrollmentDataSubject.next(null);
    this.cacheTimestamp = 0;
  }

  /**
   * Update cached data (useful after making changes)
   */
  updateCachedData(data: Partial<UserCourseDto>): void {
    const currentData = this.enrollmentDataSubject.value;
    if (currentData) {
      const updatedData = { ...currentData, ...data };
      this.enrollmentDataSubject.next(updatedData);
      this.cacheTimestamp = Date.now();
    }
  }

  /**
   * Initialize enrollment data (call this on login)
   */
  initializeEnrollmentData(): Observable<UserCourseDto> {
    return this.getEnrollmentDataWithFallback();
  }

  /**
   * Private method to fetch data from API
   */
  private fetchEnrollmentData(): Observable<UserCourseDto> {
    if (!this.authService.loggedIn()) {
      return throwError('User not authenticated');
    }

    const userId = this.authService.returnCurrentUser().id;
    const courseId = this.authService.returnCourseId();

    this.isLoadingSubject.next(true);

    return this.userClassesService.GetUserEnrolment(userId, courseId).pipe(
      tap(data => {
        // Cache the data
        this.enrollmentDataSubject.next(data);
        this.cacheTimestamp = Date.now();
        this.isLoadingSubject.next(false);
      }),
      catchError(error => {
        this.isLoadingSubject.next(false);
        return throwError(error);
      }),
      shareReplay(1) // Share the result among multiple subscribers
    );
  }
}